import React, { useEffect, useState } from "react";
import { Eye, TrendingUp, FileText } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const RANGES = [
  [7, "7 days"],
  [30, "30 days"],
  [90, "90 days"],
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/analytics/summary?days=${days}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [days]);

  const chartData = (data?.daily || []).map((d) => ({
    day: d.day,
    label: new Date(d.day).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
    count: d.count,
  }));

  const peak = Math.max(1, ...chartData.map((d) => d.count));

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-serif-display text-4xl">Visitor Stats</h1>
        <div className="flex gap-1 bg-white border border-brand-border rounded-sm p-1">
          {RANGES.map(([d, label]) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs uppercase tracking-luxe rounded-sm transition-colors ${
                days === d ? "bg-brand-ink text-white" : "text-muted-foreground hover:text-brand-ink"
              }`}
              data-testid={`analytics-range-${d}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat icon={Eye} label="Total page views" value={data.total_views} testid="stat-total-views" />
            <Stat icon={TrendingUp} label="Busiest day" value={peak} testid="stat-peak" />
            <Stat icon={FileText} label="Pages tracked" value={data.top_pages.length} testid="stat-pages" />
          </div>

          <div className="bg-white border border-brand-border rounded-sm p-6">
            <h3 className="font-serif-display text-xl mb-4">Daily visits</h3>
            {data.total_views === 0 ? (
              <p className="text-sm text-muted-foreground py-16 text-center">
                No visits recorded yet. Once shoppers browse your site, their page views will appear here.
              </p>
            ) : (
              <div style={{ width: "100%", height: 300 }} data-testid="analytics-chart">
                <ResponsiveContainer>
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="ckArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#deb5c0" stopOpacity={0.7} />
                        <stop offset="100%" stopColor="#deb5c0" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#efe7e0" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8a8178" }} axisLine={false} tickLine={false} minTickGap={20} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8a8178" }} axisLine={false} tickLine={false} width={40} />
                    <Tooltip
                      contentStyle={{ borderRadius: 2, border: "1px solid #efe7e0", fontSize: 12 }}
                      labelStyle={{ color: "#2b2b2b" }}
                    />
                    <Area type="monotone" dataKey="count" name="Views" stroke="#c58a99" strokeWidth={2} fill="url(#ckArea)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RankList
              title="Most viewed products"
              rows={data.top_products}
              testid="top-products"
              render={(p) => (
                <span className="text-sm truncate">{p.title}</span>
              )}
              empty="No product views yet."
            />
            <RankList
              title="Most visited pages"
              rows={data.top_pages}
              testid="top-pages"
              render={(p) => <span className="text-sm truncate">{p.title || p.path}</span>}
              empty="No page views yet."
            />
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-brand-border rounded-sm p-5 flex items-center gap-4" data-testid={testid}>
    <div className="w-11 h-11 rounded-full bg-brand-pink/25 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5 text-brand-ink" />
    </div>
    <div>
      <div className="font-serif-display text-3xl leading-none">{value}</div>
      <div className="text-xs uppercase tracking-luxe text-muted-foreground mt-1">{label}</div>
    </div>
  </div>
);

const RankList = ({ title, rows, render, empty, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.views));
  return (
    <div className="bg-white border border-brand-border rounded-sm p-6" data-testid={testid}>
      <h3 className="font-serif-display text-xl mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r, i) => (
            <li key={i}>
              <div className="flex items-center justify-between gap-3 mb-1">
                {render(r)}
                <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{r.views}</span>
              </div>
              <div className="h-1.5 bg-brand-bg rounded-full overflow-hidden">
                <div className="h-full bg-brand-pink rounded-full" style={{ width: `${(r.views / max) * 100}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
